import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import {
  X,
  Loader2,
  FolderKanban,
  Wrench,
  Calendar,
  Building2,
  User,
  Clock,
  DollarSign,
  Edit,
  Save,
  FileText,
  Layers,
  Tag,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PurchaseOrderManager from "./PurchaseOrderManager";
import WorkScopeManager from "./WorkScopeManager";

const STATUS_OPTIONS = {
  project: ["planning", "in_progress", "on_hold", "completed", "cancelled"],
  maintenance: ["pending", "assigned", "in_progress", "completed", "cancelled"]
};

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];

export default function EntityDetailModal({ 
  open, 
  onOpenChange,
  onClose, // Support both prop names
  entityType, // "project" or "maintenance"
  entityId,
  onUpdate 
}) {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Support both onOpenChange and onClose props
  const handleOpenChange = onOpenChange || onClose;
  
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "super_admin" || user?.role === "hub_admin";

  useEffect(() => {
    if (open && entityId) {
      fetchEntityDetail();
    }
  }, [open, entityId]);

  const fetchEntityDetail = async () => {
    setLoading(true);
    try {
      const endpoint = entityType === "project"
        ? `${API}/admin/projects/${entityId}`
        : `${API}/admin/maintenance/${entityId}`;
      
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const entityData = entityType === "project" ? data.project : data.maintenance_request;
        setEntity(entityData);
        setEditForm({
          name: entityData?.name || "",
          description: entityData?.description || "",
          status: entityData?.status || "",
          priority: entityData?.priority || "medium",
          notes: entityData?.notes || ""
        });
      } else {
        toast.error(`Failed to load ${entityType} details`);
      }
    } catch (err) {
      toast.error(`Failed to load ${entityType} details`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = entityType === "project"
        ? `${API}/admin/projects/${entityId}`
        : `${API}/admin/maintenance/${entityId}`;
      
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (res.ok) {
        toast.success(`${entityType === "project" ? "Project" : "Maintenance request"} updated`);
        setEditing(false);
        fetchEntityDetail();
        if (onUpdate) onUpdate();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (err) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      planning: "bg-blue-100 text-blue-700",
      pending: "bg-amber-100 text-amber-700",
      assigned: "bg-purple-100 text-purple-700",
      in_progress: "bg-cyan-100 text-cyan-700",
      on_hold: "bg-orange-100 text-orange-700",
      completed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "bg-slate-100 text-slate-600",
      medium: "bg-blue-100 text-blue-700",
      high: "bg-orange-100 text-orange-700",
      critical: "bg-red-100 text-red-700"
    };
    return colors[priority] || "bg-slate-100 text-slate-600";
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {entityType === "project" ? (
              <FolderKanban className="w-5 h-5 text-blue-600" />
            ) : (
              <Wrench className="w-5 h-5 text-amber-600" />
            )}
            {entityType === "project" ? "Project Details" : "Maintenance Request Details"}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : entity ? (
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="work-scope">
                <Layers className="w-4 h-4 mr-1" />
                Work Scope
              </TabsTrigger>
              <TabsTrigger value="purchase-orders">
                <FileText className="w-4 h-4 mr-1" />
                Purchase Orders
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="mt-4 space-y-6">
              {/* Header with edit toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    {entity.name || entity.request_type || "Untitled"}
                  </h3>
                  <p className="text-sm text-slate-500">ID: {entity.id}</p>
                </div>
                {isAdmin && (
                  <Button
                    variant={editing ? "default" : "outline"}
                    onClick={() => setEditing(!editing)}
                  >
                    {editing ? (
                      <>
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Status & Priority Badges */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(entity.status)}`}>
                  {entity.status?.replace('_', ' ')}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(entity.priority)}`}>
                  {entity.priority} priority
                </span>
              </div>

              {editing ? (
                /* Edit Form */
                <div className="space-y-4 bg-slate-50 rounded-lg p-4">
                  {entityType === "project" && (
                    <div>
                      <Label>Project Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Status</Label>
                      <Select
                        value={editForm.status}
                        onValueChange={(value) => setEditForm({...editForm, status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS[entityType].map(s => (
                            <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select
                        value={editForm.priority}
                        onValueChange={(value) => setEditForm({...editForm, priority: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map(p => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                      rows={2}
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <div className="space-y-4">
                  {/* Info Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {entity.hospital_name && (
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Building2 className="w-3 h-3" />
                          Hospital
                        </div>
                        <p className="font-medium text-slate-900">{entity.hospital_name}</p>
                      </div>
                    )}
                    
                    {entity.created_at && (
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <Calendar className="w-3 h-3" />
                          Created
                        </div>
                        <p className="font-medium text-slate-900">
                          {new Date(entity.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    
                    {entity.assigned_contractor && (
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <User className="w-3 h-3" />
                          Assigned To
                        </div>
                        <p className="font-medium text-slate-900">{entity.assigned_contractor}</p>
                      </div>
                    )}

                    {entity.budget && (
                      <div className="bg-white border border-slate-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                          <DollarSign className="w-3 h-3" />
                          Budget
                        </div>
                        <p className="font-medium text-slate-900">
                          ${entity.budget.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {entity.description && (
                    <div className="bg-white border border-slate-200 rounded-lg p-4">
                      <h4 className="font-medium text-slate-700 mb-2">Description</h4>
                      <p className="text-slate-600">{entity.description}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {entity.notes && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-amber-700 mb-2">Notes</h4>
                      <p className="text-amber-800">{entity.notes}</p>
                    </div>
                  )}

                  {/* Tagged Users */}
                  {entity.tagged_users?.length > 0 && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-purple-600" />
                        <h4 className="font-medium text-purple-700">Tagged Users</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {entity.tagged_users.map((userId, i) => (
                          <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                            {userId}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Work Scope Tab */}
            <TabsContent value="work-scope" className="mt-4">
              <WorkScopeManager
                entityType={entityType}
                entityId={entityId}
                entityName={entity.name || entity.request_type}
                initialWorkScope={entity.work_scope || []}
                initialTaggedUsers={[]}
                initialPendingInvites={entity.pending_invites || []}
                onUpdate={fetchEntityDetail}
              />
            </TabsContent>

            {/* Purchase Orders Tab */}
            <TabsContent value="purchase-orders" className="mt-4">
              <PurchaseOrderManager
                entityType={entityType}
                entityId={entityId}
                entityName={entity.name || entity.request_type}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-400" />
            <p>Failed to load details</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
